// /dashboard/g/<game>/analytics: players, sessions, session length, return rates and the playtime spread for the
// game's creator, from accounts' GET /v1/creators/me/games/:id/analytics?days= (wave 6). Shaping and the privacy
// rules: src/analytics.ts; the charts: src/charts.ts.

import { api, isMissingEndpoint } from "../api.ts";
import {
  clampRange,
  compact,
  dayPoints,
  fmtDay,
  gapCounts,
  gapText,
  parseAnalytics,
  pct,
  RANGES,
  retentionPoints,
  type Analytics,
  type Point,
  type Range,
} from "../analytics.ts";
import type { Game } from "../catalogue.ts";
import { bucketChart, columnChart, legend, lineChart, tableView } from "../charts.ts";
import { clear, errorBox, fmtPlaytime, h, link } from "../dom.ts";
import { kpi } from "./dashboard.ts";

export const ANALYTICS_PATH = (gameId: string, days: number) => `/creators/me/games/${encodeURIComponent(gameId)}/analytics?days=${days}`;

export function gameAnalytics(g: Game, query = new URLSearchParams(location.search)): HTMLElement {
  let days: Range = clampRange(query.get("days"));
  const pills = h("div", { class: "filter-row", role: "group", "aria-label": "Date range" });
  const body = h("div", { class: "viz-body", "aria-live": "polite" }, h("p", { class: "dim" }, "Loading…"));
  let seq = 0;
  const load = async () => {
    const mine = ++seq;
    clear(
      pills,
      RANGES.map((d) =>
        h(
          "button",
          {
            type: "button",
            class: `pill${d === days ? " on" : ""}`,
            "aria-pressed": d === days ? "true" : "false",
            onclick: () => {
              if (d === days) return;
              days = d;
              history.replaceState(null, "", `${location.pathname}?days=${d}`);
              void load();
            },
          },
          `Last ${d} days`,
        ),
      ),
    );
    // refetching keeps the previous charts, dimmed, instead of flashing
    body.classList.add("refreshing");
    try {
      const a = parseAnalytics(await api("accounts", ANALYTICS_PATH(g.id, days)), g.id);
      if (mine !== seq) return;
      clear(body, view(a, days));
    } catch (e) {
      if (mine !== seq) return;
      clear(body, isMissingEndpoint(e) ? h("p", { class: "note" }, "Analytics aren't available yet. They appear here once accounts serves them.") : errorBox(e));
    } finally {
      if (mine === seq) body.classList.remove("refreshing");
    }
  };
  void load();
  return h(
    "div",
    { class: "analytics" },
    h("h1", null, `${g.title}: Analytics`),
    h("p", { class: "dim" }, "How many people play, how long, and who comes back. Days are UTC days. ", link(`/dashboard/g/${g.id}`, null, "Back to the game")),
    pills,
    body,
  );
}

const num = (v: number) => compact(v);
const minutes = (s: number) => s / 60;
const fmtMin = (m: number) => (m >= 100 ? `${Math.round(m)}` : `${+m.toFixed(1)}`);

function view(a: Analytics, days: Range): HTMLElement {
  const few = a.minPlayers;
  const players = dayPoints(a.days, "players");
  const newPlayers = dayPoints(a.days, "newPlayers");
  const sessions = dayPoints(a.days, "sessions");
  const active = dayPoints(a.days, "activeSeconds");
  const median = dayPoints(a.days, "medianSessionSeconds").map((p) => ({ ...p, value: p.value === null ? null : minutes(p.value) }));
  const d1 = retentionPoints(a.days, "d1", Date.now(), few);
  const d7 = retentionPoints(a.days, "d7", Date.now(), few);
  const dates = players.map((p) => p.date);
  const xLabel = (i: number) => (dates[i] ? fmtDay(dates[i]!) : "");
  const t = a.totals;
  const hidden = `fewer than ${few}`;
  const ret = (r: Analytics["retention"]["d1"]) => (r?.rate != null ? pct(r.rate) : "—");
  const retSub = (r: Analytics["retention"]["d1"]) => (r?.cohort != null && r.rate != null ? `${r.returned ?? 0} of ${r.cohort} new players` : `needs ${few}+ new players and time to pass`);

  if (!a.days.length) return h("p", { class: "dim" }, "No play in this period yet.");

  const cell = (p: Point, fmt: (v: number) => string) => (p.value !== null ? fmt(p.value) : gapText(p.gap ?? "missing", few));
  const noteGaps = (pts: Point[]) => {
    const c = gapCounts(pts);
    const parts = [
      c.suppressed ? `${c.suppressed} ${c.suppressed === 1 ? "day" : "days"} had fewer than ${few} players, so ${c.suppressed === 1 ? "it's" : "they're"} hidden to protect their privacy (shaded, not zero)` : null,
      c.missing ? `${c.missing} ${c.missing === 1 ? "day has" : "days have"} no data` : null,
    ].filter(Boolean);
    return parts.length ? h("p", { class: "viz-note" }, `${parts.join("; ")}.`) : null;
  };

  return h(
    "div",
    null,
    h(
      "div",
      { class: "kpis" },
      kpi(`Players, ${days} days`, t.players === null ? hidden : num(t.players), t.newPlayers !== null ? `${num(t.newPlayers)} new` : undefined),
      kpi("Sessions", t.sessions === null ? hidden : num(t.sessions)),
      kpi("Median session", t.medianSessionSeconds === null ? "—" : fmtPlaytime(t.medianSessionSeconds)),
      kpi("Back the next day", ret(a.retention.d1), retSub(a.retention.d1)),
      kpi("Back after a week", ret(a.retention.d7), retSub(a.retention.d7)),
    ),
    t.suppressed ? h("p", { class: "viz-note" }, `Fewer than ${few} people played in this period, so the totals are hidden to protect their privacy.`) : null,

    h(
      "section",
      { class: "panel viz" },
      h("h2", null, "Players per day"),
      columnChart(players, { title: `Players per day over ${dates.length} days`, unit: "players", fmt: num, xLabel, minPlayers: few }),
      noteGaps(players),
      tableView(["Day", "Players", "New players"], players.map((p, i) => [p.date, cell(p, num), cell(newPlayers[i]!, num)])),
    ),
    h(
      "section",
      { class: "panel viz" },
      h("h2", null, "Sessions per day"),
      a.sessionGapSeconds ? h("p", { class: "dim small" }, `A session ends after ${fmtPlaytime(a.sessionGapSeconds)} without play.`) : null,
      columnChart(sessions, { title: `Sessions per day over ${dates.length} days`, unit: "sessions", fmt: num, xLabel, minPlayers: few }),
      noteGaps(sessions),
      tableView(["Day", "Sessions", "Active play"], sessions.map((p, i) => [p.date, cell(p, num), cell(active[i]!, fmtPlaytime)])),
    ),
    h(
      "section",
      { class: "panel viz" },
      h("h2", null, "Median session length"),
      h("p", { class: "dim small" }, "Half of each day's sessions were shorter than this, half longer."),
      lineChart([{ name: "Median", slot: "s1", points: median }], { title: "Median session length per day, in minutes", unit: "minutes", fmt: fmtMin, xLabel, minPlayers: few }),
      noteGaps(median),
      tableView(["Day", "Median session"], median.map((p) => [p.date, p.value !== null ? `${fmtMin(p.value)} min` : gapText(p.gap ?? "missing", few)])),
    ),
    h(
      "section",
      { class: "panel viz" },
      h("h2", null, "Who comes back"),
      h("p", { class: "dim small" }, "Of each day's new players, the share who played again the next day (day 1) and a week later (day 7)."),
      legend([
        { name: "Day 1", slot: "s1" },
        { name: "Day 7", slot: "s2" },
      ]),
      lineChart(
        [
          { name: "Day 1", slot: "s1", points: d1 },
          { name: "Day 7", slot: "s2", points: d7 },
        ],
        { title: "Share of new players who came back, day 1 and day 7", unit: "% who came back", fmt: pct, xLabel, minPlayers: few, yMax: 1 },
      ),
      retentionNote(d1, d7, few),
      tableView(["Day", "Day 1", "Day 7"], d1.map((p, i) => [p.date, cell(p, pct), cell(d7[i]!, pct)])),
    ),
    h(
      "section",
      { class: "panel viz" },
      h("h2", null, "How long people play"),
      h("p", { class: "dim small" }, `Players by their total play time over the ${days} days.`),
      a.histogram.suppressed || !a.histogram.buckets.length
        ? h("p", { class: "viz-note" }, `Fewer than ${few} people played in this period, so the spread is hidden to protect their privacy.`)
        : [
            bucketChart(
              a.histogram.buckets.map((b) => b.label),
              a.histogram.buckets.map((b) => b.players),
              { title: "Players by total play time", unit: "players", fmt: num, xLabel: (i) => a.histogram.buckets[i]?.label ?? "", minPlayers: few },
            ),
            a.histogram.buckets.some((b) => b.players === null) ? h("p", { class: "viz-note" }, `Buckets marked <${few} had fewer than ${few} players; their counts are hidden to protect their privacy.`) : null,
            tableView(["Play time", "Players"], a.histogram.buckets.map((b) => [b.label, b.players === null ? `fewer than ${few}` : num(b.players)])),
          ],
    ),
    h("p", { class: "dim small" }, "Play time counts only time with the game focused and a player at the controls."),
  );
}

function retentionNote(d1: Point[], d7: Point[], few: number): HTMLElement | null {
  const a = gapCounts(d1);
  const b = gapCounts(d7);
  const parts = [
    a.suppressed ? `${a.suppressed} ${a.suppressed === 1 ? "day" : "days"} had fewer than ${few} players and ${a.suppressed === 1 ? "is" : "are"} hidden` : null,
    a.small + b.small ? `days with fewer than ${few} new players have no share` : null,
    a.pending || b.pending ? "the most recent days can't be measured yet (day 1 needs a day to pass, day 7 a week)" : null,
  ].filter(Boolean) as string[];
  if (!parts.length) return null;
  const text = parts.join("; ");
  return h("p", { class: "viz-note" }, `${text.charAt(0).toUpperCase()}${text.slice(1)}. Those are gaps in the lines, not zero.`);
}
